import classnames from 'classnames';
import React from 'react';

import { Icon, IconTypes } from '@eika/icon';
import { Label } from '@eika/label';
import '@eika/link-list-style';

import NotificationBadge from './components/NotificationBadge';
import TextContent from './components/TextContent';

export type LinkListItemBodyProps = {
    children: React.ReactNode;
    description?: React.ReactNode;
    labels?: ReturnType<typeof Label>[] | ReturnType<typeof Label>;
    notificationNumber?: number;
    notificationScreenReaderText?: string;
    icon?: IconTypes;
    rightIcon?: IconTypes;
};

const LinkListItemBody = ({
    icon,
    children,
    labels,
    description,
    notificationNumber,
    notificationScreenReaderText,
    rightIcon = IconTypes.PIL_HOEYRE,
}: LinkListItemBodyProps) => {
    return (
        <>
            {icon && (
                <span className="sgw-link-list-item__icon">
                    <Icon type={icon} />
                </span>
            )}
            <TextContent title={children} labels={labels} description={description} />
            <span className="sgw-link-list-item__visual">
                <NotificationBadge number={notificationNumber} screenReaderText={notificationScreenReaderText} />
                <Icon type={rightIcon} />
            </span>
        </>
    );
};

export interface LinkListItemProps extends React.HTMLProps<HTMLAnchorElement> {
    href: string;
    children: React.ReactNode;
    className?: string;
    id?: string;
    target?: string;
    rel?: string;
    description?: React.ReactNode;
    labels?: ReturnType<typeof Label>[] | ReturnType<typeof Label>;
    notificationNumber?: number;
    notificationScreenReaderText?: string;
    icon?: IconTypes;
    download?: boolean;
    rightIcon?: IconTypes;
}

const LinkListItem = ({
    className,
    children,
    description,
    labels,
    notificationNumber,
    notificationScreenReaderText,
    icon,
    download = false,
    rightIcon = IconTypes.PIL_HOEYRE,
    ...rest
}: LinkListItemProps): JSX.Element => (
    <li>
        <a
            className={classnames('sgw-link-list-item', className, { 'sgw-link-list-item-icon': icon })}
            {...rest}
            rel={rest.target === '_blank' ? 'noopener noreferrer' : rest.rel}
            download={download}
        >
            <LinkListItemBody
                icon={icon}
                rightIcon={rightIcon}
                notificationNumber={notificationNumber}
                notificationScreenReaderText={notificationScreenReaderText}
                labels={labels}
                description={description}
            >
                {children}
            </LinkListItemBody>
        </a>
    </li>
);

export interface LinkListButtonProps
    extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    children: React.ReactNode;
    className?: string;
    id?: string;
    description?: React.ReactNode;
    labels?: ReturnType<typeof Label>[] | ReturnType<typeof Label>;
    notificationNumber?: number;
    notificationScreenReaderText?: string;
    icon?: IconTypes;
    rightIcon?: IconTypes;
}

const LinkListButton = ({
    className,
    children,
    description,
    labels,
    notificationNumber,
    notificationScreenReaderText,
    icon,
    rightIcon = IconTypes.PIL_HOEYRE,
    ...rest
}: LinkListButtonProps): JSX.Element => (
    <li>
        <button
            className={classnames('sgw-link-list-item', 'sgw-link-list-item--button', className, {
                'sgw-link-list-item-icon': icon,
            })}
            type="button"
            {...rest}
        >
            <LinkListItemBody
                icon={icon}
                rightIcon={rightIcon}
                notificationNumber={notificationNumber}
                notificationScreenReaderText={notificationScreenReaderText}
                labels={labels}
                description={description}
            >
                {children}
            </LinkListItemBody>
        </button>
    </li>
);

export interface LinkListProps extends React.PropsWithChildren {
    className?: string;
    id?: string;
    padding?: boolean;
    navigation?: boolean;
}

export interface LinkListComposition {
    Item: React.FC<LinkListItemProps>;
    ItemAsButton: React.FC<LinkListButtonProps>;
}

const LinkList: React.FC<LinkListProps> & LinkListComposition = ({ children, className, id, padding, navigation }) => (
    <ul
        className={classnames(
            'sgw-link-list',
            className,
            { 'with-padding': padding },
            { 'link-list--navigation': navigation },
        )}
        id={id}
    >
        {children}
    </ul>
);

LinkList.Item = LinkListItem;
LinkList.ItemAsButton = LinkListButton;

export default LinkList;
